import React, { useEffect, useState } from 'react';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Button, Col, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterSelect, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, filterHelpers, helpers } from '../utils';

export default function RoleManager() {
  const ALL_OPTION = { value: 0, label: "-- Please Choose a Role --" };
  const [data, setData] = useState([]);
  const [expanded, setExpanded] = useState("1");
  const [hasRole, setHasRole] = useState([]);
  const [id, setId] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [notInRole, setNotRole] = useState([]);
  const [filters, setFilters] = useState([
    { filterName: "role", value: ALL_OPTION }
  ]);

  useEffect(() => {
    document.title = `Role Manager`
    async function setDropdown() {
      api
        .fetch(`UserAdmin/RoleList`)
        .then(response => {
          if (response.data.success) {
            const ddl = _.concat([ALL_OPTION],
              _.map(response.data.roles, r => ({ value: r.roleId, label: r.roleName })));
            return {
              theList: ddl
            };
          } else {
            throw new Error(response);
          }
        }).then(async ({ theList }) => {
          setData(theList);
        })
        .catch((error) => {
          api.catchHandler(error);
          toast.error(error);
        })
        .finally(() => setInitialized(true))
    }
    setDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // if no role selected, just clear user lists
    if (id === 0) {
      setHasRole([]);
      setNotRole([]);
      return;
    }

    // if a role was selected, get user lists
    setLoaded(false);
    api
      .post(`UserAdmin/GetUsersForRole/${id}`)
      .then(response => {
        if (response.data.success) {
          setHasRole(response.data.hasRole);
          setNotRole(response.data.notInRole);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        api.catchHandler(error);
      })
      .finally(() => setLoaded(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function addUserRole(userId) {
    setLoaded(false);
    api
      .post(`UserAdmin/AddUserToRole/${id}/${userId}`)
      .then(response => {
        if (response.data.success) {
          setHasRole(response.data.hasRole);
          setNotRole(response.data.notInRole);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        api.catchHandler(error);
      })
      .finally(() => setLoaded(true))
  }

  function deleteUserRole(userId) {
    setLoaded(false);
    api
      .post(`UserAdmin/RemoveUserFromRole/${id}/${userId}`)
      .then(response => {
        if (response.data.success) {
          setHasRole(response.data.hasRole);
          setNotRole(response.data.notInRole);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        api.catchHandler(error);
      })
      .finally(() => setLoaded(true))
  }

  function onChange(newFilterValue) {
    let newFilters = filterHelpers.getFilters(filters, newFilterValue);
    setFilters(newFilters);
    setId(newFilterValue.value);
  }

  const toggle = (idx) => {
    if (expanded === idx) {
      setExpanded();
    } else {
      setExpanded(idx);
    }
  };

  return (
    <DesktopContainer screenName="Role Manager">
      <div className="filter-header">
        <Row className="align-items-center my-1">
          <Col xs={4}></Col>
          <Col className="h6 fw-bold" xs={4}>
            <FilterSelect
              filterName="role"
              options={data}
              value={filterHelpers.getValue(filters, "role")}
              onChangeCallback={e => onChange(e)}
            />
          </Col>
          <Col xs={4}></Col>
        </Row>
      </div>
      <br />

      <ServiceThreadSpinner on={!loaded || !initialized}>
        <div className="divCenter800px">
          <Accordion open={expanded} toggle={toggle}>
            <AccordionItem className="mobileBlueBgAlt text-white my-3 py-1 px-1">
              <AccordionHeader targetId="1">
                Users In This Role
              </AccordionHeader>
              <AccordionBody accordionId="1" className="py-1 px-3">
                <Table borderless className="m-0">
                  <tbody>
                    {hasRole?.length <= 0 ?
                      <tr>
                        <td className="data-cell my-0 py-0">
                          <label className="text-white h6 fw-bold m-0">No users were found for this role, or no role was selected.</label>
                        </td>
                      </tr>
                      : null
                    }
                    {hasRole?.length > 0 ?
                      _.map(hasRole, (item, index) => {
                        return (
                          <tr details={item} key={String(index)}>
                            <td className="data-cell my-0 py-0 width95px">
                              <Button children="Remove" className="shredLinkButton text-shredYellow" onClick={() => {
                                deleteUserRole(item.userId);
                              }} />
                            </td>
                            <td className="data-cell my-0 py-0">
                              <label className={helpers.textContains(item.userName, 'Inactive') ? "text-white h6 m-0 text-red" : "text-white h6 m-0"}>{item.userName}</label>
                            </td>
                            <td>&nbsp;</td>
                          </tr>
                        )
                      })
                      : null
                    }
                  </tbody>
                </Table>
              </AccordionBody>
            </AccordionItem>

            <AccordionItem className="mobileBlueBgAlt text-white my-3 py-1 px-1">
              <AccordionHeader targetId="2">
                Users NOT In This Role
              </AccordionHeader>
              <AccordionBody accordionId="2" className="py-1 px-3">
                <Table borderless className="m-0">
                  <tbody>
                    {notInRole?.length <= 0 ?
                      <tr>
                        <td className="data-cell my-0 py-0">
                          <label className="text-white h6 fw-bold m-0">This will populate once a role is selected.</label>
                        </td>
                      </tr>
                      : null
                    }
                    {notInRole?.length > 0 ?
                      _.map(notInRole, (item, index) => {
                        return (
                          <tr details={item} key={String(index)}>
                            <td className="data-cell my-0 py-0 width115px">
                              <Button children="Add to Role" className="shredLinkButton text-shredYellow" onClick={() => {
                                addUserRole(item.userId);
                              }} />
                            </td>
                            <td className="data-cell my-0 py-0">
                              <label className="text-white h6 m-0">{item.userName}</label>
                            </td>
                            <td>&nbsp;</td>
                          </tr>
                        )
                      })
                      : null
                    }
                  </tbody>
                </Table>
              </AccordionBody>
            </AccordionItem>

          </Accordion>
        </div>
      </ServiceThreadSpinner>
    </DesktopContainer >
  );
}